import React, { useMemo } from 'react';
import { ToggleLeft, Type, Clock, Shield, Table, Download } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import api from '../../../../lib/api';
import { buildDemographicQuestions } from '../../../../utils/chartableFields';
import { personalMessageEnabled, personalMessageQuestion } from '../../../../utils/personalMessageColumn';
import RichTextEditor from '../../../RichTextEditor';
import type { ReportFormState } from './OutreachReportWizard';

interface Props {
  itemId: string;
  formState: ReportFormState;
  onChange: (updates: Partial<ReportFormState>) => void;
}

const sectionOptions = [
  { key: 'overview_stats' as const, label: 'Overview Statistics', description: 'Total responses count' },
  { key: 'summary' as const, label: 'Summary Section', description: 'Custom rich-text summary written by you' },
  { key: 'charts' as const, label: 'Charts', description: 'The charts configured in Step 3' },
  { key: 'responses_table' as const, label: 'Responses Table', description: 'Anonymized individual response data (up to 100 rows)' }
];

const EXPIRY_OPTIONS = [
  { value: null, label: 'Never expires' },
  { value: 7, label: '7 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
  { value: 365, label: '1 year' }
];

const ReportStep4Settings: React.FC<Props> = ({ itemId, formState, onChange }) => {
  const sections = formState.sectionsJson;

  const { data: itemData } = useQuery(
    ['outreach-item-def', itemId],
    async () => {
      const res = await api.get(`/outreach-unified/items/${itemId}`);
      return res.data.data;
    },
    { staleTime: 5 * 60 * 1000 }
  );

  const questions = useMemo(() => {
    const raw = itemData?.definition_json?.questions;
    const defined = !raw || !Array.isArray(raw) ? [] : raw.map((q: any, idx: number) => ({
      id: q.id || `question_${idx}`,
      label: q.question || q.label || q.text || `Question ${idx + 1}`,
      type: q.type || 'text'
    }));
    // Personal Email Message is pickable only when the item collects it —
    // and included in a report only when explicitly checked here (the
    // "leave unchecked = all fields" default never includes it).
    const personalMessage = personalMessageEnabled(itemData?.definition_json)
      ? [personalMessageQuestion()]
      : [];
    return [...defined, ...personalMessage, ...buildDemographicQuestions(itemData?.definition_json)];
  }, [itemData]);

  const toggleSection = (key: keyof Pick<ReportFormState['sectionsJson'], 'overview_stats' | 'summary' | 'charts' | 'responses_table'>) => {
    onChange({
      sectionsJson: { ...sections, [key]: !sections[key] }
    });
  };

  const toggleResponseColumn = (questionId: string) => {
    const current = sections.response_columns || [];
    const updated = current.includes(questionId)
      ? current.filter(id => id !== questionId)
      : [...current, questionId];
    onChange({
      sectionsJson: { ...sections, response_columns: updated }
    });
  };

  const selectAllColumns = () => {
    onChange({
      sectionsJson: { ...sections, response_columns: questions.map((q: any) => q.id) }
    });
  };

  const clearAllColumns = () => {
    onChange({
      sectionsJson: { ...sections, response_columns: [] }
    });
  };

  const selectedCount = (sections.response_columns || []).length;

  return (
    <div className="space-y-6">
      {/* Report Sections */}
      <div className="card">
        <h3 className="font-medium text-text-primary dark:text-text-dark-primary flex items-center gap-2 mb-1">
          <ToggleLeft className="w-4 h-4" />
          Report Sections
        </h3>
        <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-4">
          Choose which sections to include in this report.
        </p>

        <div className="space-y-2">
          {sectionOptions.map(opt => (
            <label key={opt.key} className="flex items-start gap-3 p-3 rounded-lg hover:bg-background-tertiary dark:hover:bg-background-dark-secondary cursor-pointer transition-colors">
              <input
                type="checkbox"
                checked={sections[opt.key]}
                onChange={() => toggleSection(opt.key)}
                className="mt-0.5 rounded border-border-medium"
              />
              <div>
                <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                  {opt.label}
                </span>
                <p className="text-xs text-text-secondary dark:text-text-dark-secondary mt-0.5">
                  {opt.description}
                </p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Response Column Picker */}
      {sections.responses_table && questions.length > 0 && (
        <div className="card">
          <h3 className="font-medium text-text-primary dark:text-text-dark-primary flex items-center gap-2 mb-1">
            <Table className="w-4 h-4" />
            Response Table Columns
          </h3>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-3">
            Select which questions to display in the responses table. Leave all unchecked to show all fields.
            {personalMessageEnabled(itemData?.definition_json) && (
              <> Personal Email Message is included only when selected.</>
            )}
          </p>

          <div className="flex items-center gap-3 mb-3">
            <button
              type="button"
              onClick={selectAllColumns}
              className="text-xs text-primary-500 hover:text-primary-600 dark:hover:text-primary-400 font-medium"
            >
              Select all
            </button>
            <span className="text-text-tertiary dark:text-text-dark-tertiary">|</span>
            <button
              type="button"
              onClick={clearAllColumns}
              className="text-xs text-primary-500 hover:text-primary-600 dark:hover:text-primary-400 font-medium"
            >
              Clear all
            </button>
            <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary ml-auto">
              {selectedCount > 0 ? `${selectedCount} selected` : 'All fields shown'}
            </span>
          </div>

          <div className="space-y-1 max-h-64 overflow-y-auto">
            {questions.map((q: any) => (
              <label
                key={q.id}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-background-tertiary dark:hover:bg-background-dark-secondary cursor-pointer transition-colors"
              >
                <input
                  type="checkbox"
                  checked={(sections.response_columns || []).includes(q.id)}
                  onChange={() => toggleResponseColumn(q.id)}
                  className="rounded border-border-medium"
                />
                <div className="flex-1 min-w-0">
                  <span className="text-sm text-text-primary dark:text-text-dark-primary truncate block">
                    {q.label}
                  </span>
                  <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary capitalize">
                    {q.type}
                  </span>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Summary editor */}
      {sections.summary && (
        <div className="card">
          <h3 className="font-medium text-text-primary dark:text-text-dark-primary flex items-center gap-2 mb-1">
            <Type className="w-4 h-4" />
            Summary Content
          </h3>
          <p className="text-sm text-text-secondary dark:text-text-dark-secondary mb-3">
            Write a summary that will appear at the top of the report. You can use this to provide context or highlight key findings.
          </p>
          {/* Rendered as HTML on the preview and the public page — a plain textarea
              silently dropped every line break the author typed. */}
          <RichTextEditor
            value={formState.summaryHtml}
            onChange={(value) => onChange({ summaryHtml: value })}
            placeholder="Write a summary for this report..."
            maxLength={5000}
          />
        </div>
      )}

      {/* Viewer Downloads — author-controlled export on shared report pages.
          Absent/true = allowed (matches pre-feature behavior); false hides the
          buttons AND 403s the public export endpoint. Staff always export. */}
      <div className="card">
        <h3 className="font-medium text-text-primary dark:text-text-dark-primary flex items-center gap-2 mb-1">
          <Download className="w-4 h-4" />
          Viewer Downloads
        </h3>
        <label className="flex items-start gap-3 p-3 rounded-lg hover:bg-background-tertiary dark:hover:bg-background-dark-secondary cursor-pointer transition-colors">
          <input
            type="checkbox"
            checked={sections.allow_export !== false}
            onChange={() => onChange({
              sectionsJson: { ...sections, allow_export: sections.allow_export === false }
            })}
            className="mt-0.5 rounded border-border-medium"
          />
          <div>
            <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
              Allow viewers to export this report (PDF and Excel)
            </span>
            <p className="text-xs text-text-secondary dark:text-text-dark-secondary mt-0.5">
              When off, the download buttons are removed from the shared report page and export
              links stop working. You and your team can always download from the Reports &amp; Communications tab.
            </p>
          </div>
        </label>
      </div>

      {/* Link Expiration — only meaningful once the report is actually reachable by link */}
      <div className="card">
        <h3 className="font-medium text-text-primary dark:text-text-dark-primary flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4" />
          Link Expiration
        </h3>
        {!formState.isDefaultPublic && (
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mb-2">
            Applies if you share this report by link. It is not public today.
          </p>
        )}
        <select
          value={formState.expiresInDays ?? ''}
          onChange={(e) => onChange({ expiresInDays: e.target.value ? Number(e.target.value) : null })}
          className="input w-64"
        >
          {EXPIRY_OPTIONS.map(opt => (
            <option key={String(opt.value)} value={opt.value ?? ''}>{opt.label}</option>
          ))}
        </select>
        <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-2">
          After expiration, the link will return a &quot;link expired&quot; message. You can always update this later.
        </p>
      </div>

      {/* Security note — worded for whether the report is actually exposed yet */}
      <div className={`flex items-start gap-2 p-3 rounded-lg ${
        formState.isDefaultPublic
          ? 'bg-amber-50 dark:bg-amber-900/20'
          : 'bg-background-tertiary dark:bg-background-dark-tertiary'
      }`}>
        <Shield className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
          formState.isDefaultPublic ? 'text-amber-500' : 'text-text-tertiary'
        }`} />
        <p className={`text-sm ${
          formState.isDefaultPublic
            ? 'text-amber-700 dark:text-amber-300'
            : 'text-text-secondary dark:text-text-dark-secondary'
        }`}>
          {formState.isDefaultPublic
            ? 'This report is public — anyone with the link can view its data. PII fields are automatically redacted from the responses table. Share responsibly.'
            : 'This report is not public. If you share it later, anyone with the link can view its data; PII fields are automatically redacted from the responses table.'}
        </p>
      </div>
    </div>
  );
};

export default ReportStep4Settings;
